import { CloseOutlined } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Icon,
} from '@mui/material'
import SelectInput from '../../Common/SelectInput'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch } from '../../../redux/hook'
import { fetchSubTemplates } from '../../../services/entityInstanceServices'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'

interface TemplateData {
    templateId: string
    templateName: string
}
interface InstanceSubTemplatesListProps {
    onClose: any
    onSubmit: (templateData: TemplateData) => void
    orgId: string
    templateId: string
}

const InstanceSubTemplatesList = (
    props: InstanceSubTemplatesListProps,
): ReactElement => {
    const { t } = useTranslation()
    const { onClose, onSubmit, orgId, templateId } = props

    const dispatch = useAppDispatch()

    const [templateData, setTemplateData] = useState<TemplateData>()
    const [totalTemplates, setTotalTemplates] = useState<any[]>([])

    const [isOpenDialog, setIsOpenDislog] = useState<boolean>(false)

    useEffect(() => {
        const setSubTemplateDetails = async () => {
            dispatch(updateLoader(true))
            const getSubTemplates: any = await fetchSubTemplates(
                orgId,
                templateId,
            )

            let updatedTotalTemplates: any = []
            if (getSubTemplates?.length) {
                updatedTotalTemplates = flattenEntityTemplates(getSubTemplates)
                updatedTotalTemplates = updatedTotalTemplates.filter(
                    (i: any) => !i.isAbstract,
                )
            }
            if (updatedTotalTemplates?.length === 1) {
                onSubmit({
                    templateId: updatedTotalTemplates[0]?.templateId,
                    templateName: updatedTotalTemplates[0]?.templateName,
                })
            } else {
                setTemplateData({
                    templateId: updatedTotalTemplates[0]?.templateId || '',
                    templateName: updatedTotalTemplates[0]?.templateName || '',
                })
                setTotalTemplates(updatedTotalTemplates || [])

                setIsOpenDislog(true)
            }
            dispatch(updateLoader(false))
        }

        if (orgId && templateId) {
            setSubTemplateDetails()
        }
    }, [orgId, templateId])

    const flattenEntityTemplates = (entityTemplates: any[]) => {
        const allSubEntityTypes: any[] = []

        const traverse = (entityTemplates: any[]) => {
            for (const entityTemplate of entityTemplates) {
                allSubEntityTypes.push(entityTemplate)
                if (entityTemplate.subTemplates) {
                    traverse(entityTemplate.subTemplates)
                }
            }
        }

        traverse(entityTemplates)
        return allSubEntityTypes
    }

    const onChangeTemplate = (id: string, value: string) => {
        const getTemplateDetails: any = totalTemplates.find(
            (i: any) => i.templateId === value,
        )
        setTemplateData((prev) => ({
            ...prev,
            templateId: getTemplateDetails?.templateId || '',
            templateName: getTemplateDetails?.templateName || '',
        }))
    }

    const onSubmitSubTemplate = () => {
        onSubmit(templateData || { templateId: '', templateName: '' })
        setIsOpenDislog(false)
        setTotalTemplates([])
    }

    const onCloseSubTemplate = () => {
        onClose()
        setIsOpenDislog(false)
        setTotalTemplates([])
    }

    return (
        <Dialog
            open={isOpenDialog}
            scroll="paper"
            PaperProps={{
                sx: {
                    width: '400px',
                },
            }}
            aria-labelledby="instance-sub-template-dialog-title"
            aria-describedby="instance-sub-template-dialog-description"
            sx={{ padding: '10px 30px' }}
        >
            <Box>
                <DialogTitle
                    id="instance-sub-template-dialog-title"
                    sx={{ padding: '0px' }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        padding="15px"
                        paddingLeft="20px"
                    >
                        <Icon
                            onClick={() => onCloseSubTemplate()}
                            sx={{ marginLeft: 'auto' }}
                            fontSize="small"
                            id="instance-sub-template-close-button"
                            aria-label={t('close') || 'Close'}
                            title={t('close') || 'Close'}
                        >
                            <CloseOutlined
                                sx={{
                                    color: '#000',
                                    marginTop: '-2px',
                                }}
                            />
                        </Icon>
                    </Box>
                </DialogTitle>
                <DialogContent
                    id="instance-sub-template-dialog-description"
                    sx={{
                        height: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ width: 'calc(100%)' }}>
                            <SelectInput
                                label={t('type')}
                                id="subtemplatesList"
                                value={templateData?.templateId || ''}
                                onChange={onChangeTemplate}
                                list={totalTemplates.map((i: any) => {
                                    return {
                                        ...{
                                            id: i.templateId,
                                            name: i.templateName,
                                        },
                                    }
                                })}
                                hasEmpty={false}
                                //    errorMessage={''}
                            />
                        </Box>
                        <Box>
                            <Button
                                sx={{
                                    mt: 4,
                                }}
                                onClick={() => onSubmitSubTemplate()}
                                variant="contained"
                                fullWidth
                                id="instance-sub-template-list-submit-button"
                                disabled={!templateData?.templateId}
                            >
                                {t('add')}
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Box>
        </Dialog>
    )
}

export default InstanceSubTemplatesList
